import { useState, useEffect, useCallback } from 'react';
import { DateCondition, ErrorCondition, Options } from '../pages/mta/types';
import { format, getTime, isEqual } from 'date-fns';
import { DISPLAY_DATE_TIME } from '../utils/constants';
import { setIsMtaFutureDate } from '../store/midTermAdjustment.slice';
import { useAppDispatch } from './reduxHook';
import { setNextValidFutureDateTime } from '../utils/formDateValueUtils';

export const useFutureDateTime = (
	initialDate: Date,
	futureDatePicker: boolean,
	{ acceptMidnight = false }: Options = {},
) => {
	const [hour, setHour] = useState<string>('00');
	const [minute, setMinute] = useState<string>('00');
	const [error, setError] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [mtaStartDate, setMtaStartDate] = useState<Date | null>(initialDate);
	const [mtaDateTime, setMtaDateTime] = useState<Date>(initialDate);
	const dispatch = useAppDispatch();

	const handleFutureDateTime = useCallback(
		(mtaStartDate: Date, hour: string, minute: string) => {
			setError(false);
			setErrorMessage('');
			const currDate = new Date();
			const date = new Date(mtaStartDate);
			const timeChange: boolean = hour == '00' && minute == '00';
			const timeDayChange: boolean = isEqual(
				currDate.getDate() + currDate.getMonth(),
				date.getDate() + date.getMonth(),
			);

			const dateConditions: DateCondition[] = [
				{
					condition: (date, currDate, timeChange) => isEqual(date, currDate) && timeChange,
					action: (date) => {
						date.setHours(date.getHours());
						date.setMinutes(date.getMinutes());
					},
				},
				{
					condition: (date, currDate) => isEqual(date.getDay(), currDate.getDay()),
					action: (date, hour, minute) => {
						date.setHours(Number(hour));
						date.setMinutes(Number(minute));
					},
				},
				{
					condition: (_1, _2, timeChange) => timeChange && acceptMidnight,
					action: (date) => {
						date.setHours(0);
						date.setMinutes(0);
					},
				},
				{
					condition: () => true,
					action: (date, hour, minute) => {
						date.setHours(date.getHours() + Number(hour));
						date.setMinutes(date.getMinutes() + Number(minute));
					},
				},
			];

			for (const { condition, action } of dateConditions) {
				if (condition(date, currDate, timeChange)) {
					action(date, hour, minute);
					break;
				}
			}
			const errorConditions: ErrorCondition[] = [
				{
					condition: (_, __, timeChange, timeDayChange) => timeChange && timeDayChange && futureDatePicker,
					action: () => {
						setError(true);
						setErrorMessage(`Please select the time you want the change to start from`);
					},
				},
				{
					condition: (date, currDate, timeChange) => {
						const timeDiff = getTime(date) - getTime(currDate);
						const fifteenMinutesInMs = 15 * 60 * 1000;
						return (
							date &&
							!timeChange &&
							futureDatePicker &&
							timeDiff < fifteenMinutesInMs && // Selected time is less than 15 minutes from now
							timeDiff >= 0 // But is still in the future (not past)
						);
					},
					action: () => {
						setError(true);
						setErrorMessage(
							`Please select a date on or after ${format(
								setNextValidFutureDateTime(currDate, 15).date,
								DISPLAY_DATE_TIME,
							)} (Next 15 min interval time)`,
						);
					},
				},
			];

			for (const { condition, action } of errorConditions) {
				if (condition(date, currDate, timeChange, timeDayChange)) {
					action();
					break;
				}
			}

			return new Date(date);
		},
		[mtaStartDate, hour, minute, acceptMidnight, futureDatePicker],
	);

	useEffect(() => {
		const currDate = new Date();
		const timeChange = hour == '00' && minute == '00';
		const selectedFutureDateTime = handleFutureDateTime(mtaStartDate ?? currDate, hour, minute);

		if (futureDatePicker && !isEqual(selectedFutureDateTime, currDate) && (!timeChange || acceptMidnight)) {
			setMtaDateTime(selectedFutureDateTime);
			dispatch(setIsMtaFutureDate(true));
		} else {
			setMtaDateTime(initialDate);
			dispatch(setIsMtaFutureDate(false));
		}
	}, [handleFutureDateTime, futureDatePicker]);

	return {
		hour,
		setHour,
		minute,
		setMinute,
		error,
		errorMessage,
		mtaStartDate,
		setMtaStartDate,
		mtaDateTime,
		setMtaDateTime,
		handleFutureDateTime,
	};
};
